import axios from 'axios';

// Global state for tracking API performance
let apiPerformanceStats = {
  slowRequests: 0,
  totalRequests: 0,
  lastSlowRequest: null,
  slowRequestThreshold: 3000, // 3 seconds
  notificationCooldown: 30000, // 30 seconds between notifications
  lastNotificationTime: 0
};

// Function to show server issue notification
const showServerIssueNotification = () => {
  const now = Date.now();
  
  // Check cooldown to avoid spam
  if (now - apiPerformanceStats.lastNotificationTime < apiPerformanceStats.notificationCooldown) {
    return;
  }
  
  // Only show notification if we have multiple slow requests
  if (apiPerformanceStats.slowRequests >= 2) {
    // Dispatch custom event for NetworkMonitor to handle
    const event = new CustomEvent('apiPerformanceIssue', {
      detail: {
        message: 'Наблюдаются технические неполадки на сервере',
        type: 'warning',
        slowRequests: apiPerformanceStats.slowRequests,
        totalRequests: apiPerformanceStats.totalRequests
      }
    });
    
    window.dispatchEvent(event);
    apiPerformanceStats.lastNotificationTime = now;
  }
};

// Request interceptor
axios.interceptors.request.use(
  (config) => {
    // Add timestamp to track request duration
    config.metadata = { startTime: new Date() };
    apiPerformanceStats.totalRequests++;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
axios.interceptors.response.use(
  (response) => {
    const endTime = new Date();
    const duration = endTime - response.config.metadata.startTime;
    
    // Check if request was slow
    if (duration > apiPerformanceStats.slowRequestThreshold) {
      apiPerformanceStats.slowRequests++;
      apiPerformanceStats.lastSlowRequest = {
        url: response.config.url,
        duration: duration,
        timestamp: endTime
      };
      
      showServerIssueNotification();
    }
    
    return response;
  },
  (error) => {
    const endTime = new Date();
    const duration = endTime - (error.config?.metadata?.startTime || endTime);
    
    // Check if request was slow even if it failed
    if (duration > apiPerformanceStats.slowRequestThreshold) {
      apiPerformanceStats.slowRequests++;
      apiPerformanceStats.lastSlowRequest = {
        url: error.config?.url || 'unknown',
        duration: duration,
        timestamp: endTime,
        error: true
      };
      
      showServerIssueNotification();
    }
    
    return Promise.reject(error);
  }
);

// Fetch API interceptor
export const setupFetchInterceptor = () => {
  const originalFetch = window.fetch;

  window.fetch = function(...args) {
    let url;
    let method = 'GET';
    let options = {};
    const startTime = new Date();
  
    // Extract URL and method from arguments
    if (typeof args[0] === 'string') {
      url = args[0];
      options = args[1] || {};
      method = options.method || 'GET';
    } else if (args[0] instanceof Request) {
      const request = args[0];
      url = request.url;
      method = options.method || request.method;
      options = args[1] || {};
    }

    // Track request
    apiPerformanceStats.totalRequests++;
  
    // Use original window.fetch to perform the request
    return originalFetch.apply(this, args)
      .then(response => {
        const endTime = new Date();
        const duration = endTime - startTime;
        
        // Check if request was slow
        if (duration > apiPerformanceStats.slowRequestThreshold) {
          apiPerformanceStats.slowRequests++;
          apiPerformanceStats.lastSlowRequest = {
            url,
            duration,
            timestamp: endTime
          };
          
          showServerIssueNotification();
        }

        return response;
      })
      .catch(error => {
        const endTime = new Date();
        const duration = endTime - startTime;
        
        // Check if request was slow even if it failed
        if (duration > apiPerformanceStats.slowRequestThreshold) {
          apiPerformanceStats.slowRequests++;
          apiPerformanceStats.lastSlowRequest = {
            url: url || 'unknown',
            duration,
            timestamp: endTime,
            error: true
          };
          
          showServerIssueNotification();
        }
        
        throw error;
      });
  };

  return () => {
    window.fetch = originalFetch; // Method to restore original fetch if needed
  };
};

// Function to reset stats (can be called periodically)
export const resetApiPerformanceStats = () => {
  apiPerformanceStats = {
    ...apiPerformanceStats,
    slowRequests: 0,
    totalRequests: 0,
    lastSlowRequest: null
  };
};

// Function to get current stats
export const getApiPerformanceStats = () => {
  return { ...apiPerformanceStats };
};

// Function to update threshold
export const setSlowRequestThreshold = (threshold) => {
  apiPerformanceStats.slowRequestThreshold = threshold;
};

// Function to update notification cooldown
export const setNotificationCooldown = (cooldown) => {
  apiPerformanceStats.notificationCooldown = cooldown;
};

export default axios; 